'use client'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { searchSuggestionsAction } from '../actions/search.actions'
import { MIN_SEARCH_QUERY_LENGTH } from '../search.constants'
import type { SearchResult } from '../types/search.types'
import { queryCachePolicy } from '@/shared/state/query-cache-policy'

/**
 * Debounced typeahead for the **top nav search bar** (`GlobalSearchOverlay` /
 * `SearchBody` only). Fans out to entity APIs server-side via
 * `searchSuggestionsAction` (business, hive, event, user, product, service).
 *
 * This hook owns the ONE debounce — callers must pass the raw input value.
 * (Callers used to debounce 200ms on top of this hook's 200ms for ~400ms of
 * lag.) `placeholderData` keeps the previous keystroke's rows on screen while
 * the next fetch is in flight instead of flashing the panel empty.
 *
 * `includePeople: false` (public surfaces) skips the JWT-gated people route
 * server-side — guests otherwise took a guaranteed 401 per keystroke.
 */
export function useSearchSuggestions(
  query: string,
  options?: { debounceMs?: number; includePeople?: boolean }
) {
  const debounceMs = options?.debounceMs ?? 200
  const includePeople = options?.includePeople ?? true
  const [debounced, setDebounced] = useState(query)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), debounceMs)
    return () => clearTimeout(id)
  }, [query, debounceMs])

  const result = useQuery<SearchResult[]>({
    queryKey: ['search', 'suggestions', debounced, includePeople],
    queryFn: async () => {
      const res = await searchSuggestionsAction(debounced, { includePeople })
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    enabled: debounced.trim().length >= MIN_SEARCH_QUERY_LENGTH,
    staleTime: queryCachePolicy.ephemeral.staleTime,
    placeholderData: (previousData) => previousData,
  })
  // `query` says which (debounced) input the rows answer, so a submit can tell
  // loaded rows for what was typed from rows still showing for the previous keystroke.
  return { ...result, query: debounced.trim() }
}
